import React, { useEffect, useState } from 'react';
import RichTextEditor from '../../component/suneditor/RichTextEditor';
import BlogService from '../../services/BlogServices';
import { toast } from 'react-toastify';
import BlogImageUploader from '../../component/formModal/BlogImageUploader';

const BlogCreationScreen = ({blog, selectedWebsite, setCurrScreen, loginUser}) => {

  // Check if the blog is approved
  const isApproved = blog?.status === 'APPROVED';
  
  const [formData, setFormData] = useState({
    title: '',
    metaTitle: '',
    metaDescription: '',
    keywords: '',
    content: '',
    author: '',
    authorDescription: '',
    schema: '',
    coverImageUrl: '',
    coverImageAlt: '',
  });
 
  // Update formData when the blog prop changes
  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title || '',
        metaTitle: blog.metaTitle || '',
        metaDescription: blog.metaDescription || '',
        keywords: blog.keywords || '',
        content: blog.content || '',
        author: blog.author || '',
        authorDescription: blog.authorDescription || '',
        schema: blog.schema || '',
        coverImageUrl: blog.coverImageUrl || '',
        coverImageAlt: blog.coverImageAlt || '',
      });
    }
  }, [blog]);

  // Handle input field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {    
    e.preventDefault();

    if (!selectedWebsite || !selectedWebsite.value) {
      toast.error('Please select a website.');
      return;
    }

    try {
      if (blog) {
        await BlogService.updateBlog(selectedWebsite?.value, blog.id, formData);
        toast.info('Blog updated successfully.');
      } else {
        await BlogService.createBlog(selectedWebsite?.value, formData);
        toast.info('Blog added successfully.');
      }
      setCurrScreen();
    } catch (error) {
      toast.error(error.message || 'Failed to save Blog.');
    }
  };

  const handleReject = async () => {
    try {
        await BlogService.rejectBlog(selectedWebsite?.value, blog.id);
        toast.info('Blog Rejected.');
        setCurrScreen();
    } catch (error) {
        toast.error(error.message || 'Failed to delete user.');
    }
  };

  return (
    <div>
      {/* Blog Creation Form */}
      <form onSubmit={handleSubmit}>
        {/* Title */}
        <div className="mb-3">
          <label htmlFor="title" className="form-label">
            Blog Title
          </label>
          <input
            type="text"
            id="title"
            name="title"
            className="form-control"
            value={formData.title}
            onChange={handleInputChange}
            disabled={isApproved}
            required
          />
        </div>

        {/* Meta Title */}
        <div className="mb-3">
          <label htmlFor="metaTitle" className="form-label">
            Meta Title
          </label>
          <input
            type="text"
            id="metaTitle"
            name="metaTitle"
            className="form-control"
            value={formData.metaTitle}
            onChange={handleInputChange}
            disabled={isApproved}
          />
        </div>

        {/* Meta Description */}
        <div className="mb-3">
          <label htmlFor="metaDescription" className="form-label">
            Meta Description
          </label>
          <textarea
            id="metaDescription"
            name="metaDescription"
            className="form-control"
            rows="3"
            value={formData.metaDescription}
            onChange={handleInputChange}
            disabled={isApproved}
          />
        </div>

        {/* Keywords */}
        <div className="mb-3">
          <label htmlFor="keywords" className="form-label">
            Keywords
          </label>
          <input
            type="text"
            id="keywords"
            name="keywords"
            className="form-control"
            placeholder="Comma-separated keywords"
            value={formData.keywords}
            onChange={handleInputChange}
            disabled={isApproved}
          />
        </div>

        {/* Author */}
        <div className="mb-3">
          <label htmlFor="author" className="form-label">
            Author Name
          </label>
          <input
            type="text"
            id="author"
            name="author"
            className="form-control"
            value={formData.author}
            onChange={handleInputChange}
            disabled={isApproved}
          />
        </div>

        {/* Author Description */}
        <div className="mb-3">
          <label htmlFor="authorDescription" className="form-label">
            Author Description
          </label>
          <textarea
            id="authorDescription"
            name="authorDescription"
            className="form-control"
            rows="2"
            value={formData.authorDescription}
            onChange={handleInputChange}
            disabled={isApproved}
          />
        </div>

        {/* Content Editor */}
        <div className="mb-3">
          <label htmlFor="content" className="form-label">
            Content
          </label>
          <RichTextEditor
            value={formData.content}
            onChange={(content) =>
              setFormData((prev) => ({ ...prev, content }))
            }
          />
        </div>

        {/* Schema */}
        <div className="mb-3">
          <label htmlFor="schema" className="form-label">
            Blog Schema (JSON)
          </label>
          <textarea
            id="schema"
            name="schema"
            className="form-control"
            rows="4"
            placeholder="Enter blog schema in JSON format"
            value={formData.schema}
            onChange={handleInputChange}
            disabled={isApproved}
          />
        </div>
        
        {/* Cover Image Alt */}
        <div className="mb-3">
          <label htmlFor="coverImageAlt" className="form-label">
            Cover Image Alt
          </label>
          <input
            type="text"
            id="coverImageAlt"
            name="coverImageAlt"
            className="form-control"
            value={formData.coverImageAlt}
            onChange={handleInputChange}
            disabled={isApproved}
          />
          <span className="text-muted">250 character limit</span>
        </div>

        <div className="mb-3">
        <BlogImageUploader
            coverImageUrl={formData.coverImageUrl}
            onChange={(url) => setFormData((prev) => ({ ...prev, coverImageUrl: url }))}
            isEditable={!isApproved}
        />
        </div>
        {/* Submit Button */}
        <button type="submit" className="btn btn-primary" disabled={blog?.status==='APPROVED'}>
            {loginUser?.role==='EXECUTIVE' ? 'Submit Blog' : 'Publish Blog'}
        </button>

        {blog && (loginUser.role === 'ADMIN' || loginUser.role === 'MANAGER') && 
        (blog.status === 'PENDING' || blog.status === 'APPROVED') && (
            <button type="button" className="btn btn-danger ms-2" onClick={handleReject}>
                {blog.status==='PENDING' ? 'Reject' : 'Unapproved'}
            </button>
        )}

      </form>
    </div>
  );
};

export default BlogCreationScreen;
